import React from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarMenuItem from './SidebarMenuItem';
import AccordionMenuItem from './AccordionMenuItem';
import MenuCard from './MenuCard';
import TokenSection from './TokenSection';
import CasinoIcon from '@/components/icons/CasinoIcon';
import SportsIcon from '@/components/icons/SportsIcon';
import SportsExchangeIcon from '@/components/icons/SportsExchangeIcon';
import GameIcon from '@/components/icons/GameIcon';
import PromotionIcon from '@/components/icons/PromotionIcon';
import VipIcon from '@/components/icons/VipIcon';
import BonusIcon from '@/components/icons/BonusIcon';
import AffiliateIcon from '@/components/icons/AffiliateIcon';
import ProvablyIcon from '@/components/icons/ProvablyIcon';
import SupportIcon from '@/components/icons/SupportIcon';
import GiftIcon from '@/components/icons/GiftIcon';
import { useGames } from '@/Context/GameContext';
import { useCrispChat } from '@/hooks';
import styles from './Sidebar.module.css';

/**
 * MenuContent Component
 * Contains the token section and all menu navigation items
 */
const MenuContent = ({ isCollapsed, onItemClick }) => {
  const navigate = useNavigate();
  const { handleJsGameLaunch } = useGames();
  const { handleSupportClick } = useCrispChat();

  // E-sports game ID (TFGaming)
  const ESPORTS_GAME_ID = '4ee8e0051a035b463b47c3c473ce317d';
  const SPORTSBOOK = '4d31f1186a81e208c003a7e37411ce35';

  // Handle e-sports click
  const handleEsportsClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Call onItemClick if provided (e.g., to close sidebar)
    if (onItemClick) {
      onItemClick();
    }
    let provider = 'ESPORTS';

    // Launch e-sports game
    await handleJsGameLaunch(ESPORTS_GAME_ID, navigate, provider);
  };

  const handleSportsBookClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Call onItemClick if provided (e.g., to close sidebar)
    // if (onItemClick) {
    //   onItemClick();
    // }

    console.log("handleSportsBookClick")
    let provider = 'SPORTSBOOK';

    // Launch e-sports game
    await handleJsGameLaunch(SPORTSBOOK, navigate, provider);
  };

  // Handle support click - opens Crisp chat
  const handleSupportClickWrapper = (e) => {
    handleSupportClick(e);

    // Call onItemClick if provided (e.g., to close sidebar)
    if (onItemClick) {
      onItemClick();
    }
  };
  // Casino sub-items - Updated to match new Casino page structure
  const casinoSubItems = [
    { name: 'Lobby', path: '/casino', icon: <CasinoIcon size="16px" /> },
    { name: 'Live Casino', path: '/casino/live-casino', icon: <CasinoIcon size="16px" /> },
    { name: 'Popular Slots', path: '/casino/popular-slots', icon: <CasinoIcon size="16px" /> },
    { name: 'Game Shows', path: '/casino/game-shows', icon: <CasinoIcon size="16px" /> },
    { name: 'Crash Games', path: '/casino/crash-games', icon: <CasinoIcon size="16px" /> },
  ];

  // Sports sub-items with sport IDs (matching tabToSportId from old code)
  const sportsSubItems = [
    { name: 'Soccer', path: '/sports/in-play/soccer', icon: <SportsIcon size="16px" />, sportId: '1' },
    { name: 'Tennis', path: '/sports/in-play/tennis', icon: <SportsIcon size="16px" />, sportId: '2' },
    { name: 'Basketball', path: '/sports/in-play/basketball', icon: <SportsIcon size="16px" />, sportId: '7522' },
    { name: 'Cricket', path: '/sports/in-play/cricket', icon: <SportsIcon size="16px" />, sportId: '4' },
    { name: 'FIFA', path: '/sports/in-play/fifa', icon: <SportsIcon size="16px" />, sportId: null }, // eSoccer - needs mapping
    { name: 'American Football', path: '/sports/in-play/american-football', icon: <SportsIcon size="16px" />, sportId: '6423' },
    { name: 'Ice Hockey', path: '/sports/in-play/ice-hockey', icon: <SportsIcon size="16px" />, sportId: null }, // Needs mapping
    { name: 'Baseball', path: '/sports/in-play/baseball', icon: <SportsIcon size="16px" />, sportId: '7511' },
    { name: 'Handball', path: '/sports/in-play/handball', icon: <SportsIcon size="16px" />, sportId: null }, // Needs mapping
    { name: 'Racing', path: '/sports/in-play/racing', icon: <SportsIcon size="16px" />, sportId: '7' }, // Horse Racing
  ];

  // VIP Club, Bonus, and Affiliate card items
  const vipBonusAffiliateItems = [
    {
      name: 'VIP Club',
      icon: <VipIcon size="20px" />,
      path: '/vip-club',
      color: 'var(--color-vip)',
      isVip: true
    },
    {
      name: 'Bonus',
      icon: <BonusIcon size="20px" />,
      path: '/bonus-page',
      color: 'var(--color-text-primary)'
    },
    {
      name: 'Affiliate',
      icon: <AffiliateIcon size="20px" />,
      path: '/affiliate',
      color: 'var(--color-text-primary)'
    },
  ];

  return (
    <>
      {/* Token Section */}
      <div className={styles.tokenWrapper}>
        <TokenSection isCollapsed={isCollapsed} />
      </div>

      {/* Menu Items */}
      <nav className={styles.nav}>
        {/* Originals */}
        {/* <SidebarMenuItem
          icon={<GameIcon size="20px" />}
          label="Originals"
          path="/games"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        /> */}

        {/* Casino - Standalone with Accordion */}
        <AccordionMenuItem
          icon={<CasinoIcon size="20px" />}
          label="Casino"
          path="/casino"
          subItems={casinoSubItems}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          onItemClick={onItemClick}
        />
        <SidebarMenuItem
          icon={<SportsExchangeIcon size="20px" />}
          label="Sportsbook"
          onClick={handleSportsBookClick}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Sports - Standalone with Accordion */}
        {/* <AccordionMenuItem
          icon={<SportsIcon size="20px" />}
          label="Sports"
          path="/sports/popular"
          subItems={sportsSubItems}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          onItemClick={onItemClick}
        /> */}

        {/* Sports Exchange - Standalone (E-sports) */}
        <SidebarMenuItem
          icon={<SportsExchangeIcon size="20px" />}
          label="E-sports"
          onClick={handleEsportsClick}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Promotions - Standalone */}
        {/* <SidebarMenuItem
          icon={<PromotionIcon size="20px" />}
          label="Promotions"
          path="/promotions"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        /> */}

        {/* Gift Cards - Standalone */}
        <SidebarMenuItem
          icon={<GiftIcon size="20px" />}
          label="Gift Cards"
          path="/gift-cards"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* VIP Club, Bonus, and Affiliate Card */}
        <MenuCard
          items={vipBonusAffiliateItems}
          isCollapsed={isCollapsed}
        />

        {/* Provably Fair */}
        <SidebarMenuItem
          icon={<ProvablyIcon size="20px" />}
          label="Provably Fair"
          path="/provably-fair"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Live Support */}
        <SidebarMenuItem
          icon={<SupportIcon size="20px" />}
          label="Live Support"
          onClick={handleSupportClickWrapper}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />
      </nav>
    </>
  );
};

export default MenuContent;
